import { useCallback, useEffect, useRef, useState } from "react";
import { merge } from "./merge";
import { SCHEMA_VERSION, upgradeDoc } from "./doc";

export const ENABLED_KEY = "syncEnabled";
export const RECONNECT_KEY = "syncNeedsReconnect";
// When the recorded failure behind RECONNECT_KEY happened. A failed silent
// attempt has to suppress further automatic ones (else a retry storm), but
// forever is worse: one bad redirect would brick sync permanently, with
// nothing to clear the flag but a disconnect the user has no reason to try.
// So a failure expires after RECONNECT_EXPIRY_MS and the boot effect gives it
// one more silent try.
export const RECONNECT_AT_KEY = "syncNeedsReconnectAt";
export const ACCOUNT_KEY = "syncAccount";
// Must match ERROR_KEY in drive.js. Duplicated rather than imported, same
// reason as the PENDING_KEY literal below: importing anything from drive.js at
// module scope would pull it into the main bundle, defeating the dynamic
// import that keeps a signed-out app's bundle Google-free.
export const ERROR_KEY = "syncAuthError";
const RECONNECT_EXPIRY_MS = 12 * 60 * 60 * 1000;
// Not a Google error at all — the redirect itself never came back (captive
// portal, lost tab, force-quit mid-flight). Worth telling apart from a Google
// error code, since the fix is different (try again vs. Google is refusing).
export const LOST_REDIRECT_REASON = "The sign-in redirect never came back.";
const DEBOUNCE_MS = 5000;
// Floor between automatic triggers (visibilitychange, focus, online). `focus`
// in particular can fire rapidly — alt-tabbing back and forth — so without a
// floor a burst of several events within a second or two would each start a
// sync, at two Drive requests apiece. The floor is sized to stop that burst,
// not to act as a polling interval: it is not "safer" the bigger it is, and a
// value much larger than the burst it defends against just makes glancing
// away for a few seconds and coming back look like sync silently broke (this
// was reported as exactly that, at the previous 30s value). Does not apply to
// the manual button (its whole purpose is to always do something) or to the
// post-edit debounce (a tick the user just made should always be pushed).
const AUTO_SYNC_FLOOR_MS = 5 * 1000;

export const isSyncEnabled = () => localStorage.getItem(ENABLED_KEY) === "true";

export function useSync({ doc, onMerged }) {
  const [enabled, setEnabled] = useState(isSyncEnabled);
  const [status, setStatus] = useState(() => (localStorage.getItem(RECONNECT_KEY) === "1" ? "reconnect" : "idle"));
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  // Why the last attempt failed, for Settings to show as a diagnostic line
  // under the paused note. Seeded from localStorage for the same reason
  // `status` is: it must be right on the very first render, not one tick later.
  const [reason, setReason] = useState(() => localStorage.getItem(ERROR_KEY));
  // Seeded from localStorage so a reload shows the account immediately, with no
  // refetch. Display only — never consulted for anything that affects syncing.
  const [account, setAccount] = useState(() => localStorage.getItem(ACCOUNT_KEY));

  // Refs so the run always sees the latest document and callback, without being
  // rebuilt each render and re-triggering its own effects.
  const docRef = useRef(doc);
  docRef.current = doc;
  const onMergedRef = useRef(onMerged);
  onMergedRef.current = onMerged;
  const fileIdRef = useRef(null);
  const runningRef = useRef(false);
  // A run requested while one was already in flight. Collapsed to a single
  // flag rather than a queue, so a burst of ticks made mid-sync causes exactly
  // one follow-up run, not one per tick.
  const rerunRequestedRef = useRef(false);
  // Session-scoped: set when a Drive call finds the token expired. Unlike
  // RECONNECT_KEY this is never persisted, because an expired token is not a
  // failed *attempt* — the next launch still gets its silent renewal (spec
  // §4.2). It only stops further doomed requests in *this* session until the
  // user reconnects or the page reloads.
  const needsAuthRef = useRef(false);
  // When a sync last completed successfully, for the automatic-trigger floor
  // below. A ref, not state: it must not cause a re-render on every sync.
  const lastSyncCompletedAtRef = useRef(0);
  // Guards the account-email `.then` below against setting state (or writing
  // the cache) after this hook is gone. Set in the effect body, not at
  // declaration: StrictMode mounts, unmounts and remounts once in development,
  // and a ref initialised only at declaration would stay `false` forever after
  // that cycle, silently disabling the feature in dev.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const syncNow = useCallback(async () => {
    if (runningRef.current) {
      rerunRequestedRef.current = true;
      return false;
    }
    // An expired token noticed mid-session: stop retrying here, but leave
    // RECONNECT_KEY alone so a future launch still tries its silent renewal.
    if (needsAuthRef.current) {
      setStatus("reconnect");
      return false;
    }
    // The debounce effect reads this flag when it is armed, which is before the
    // boot effect has decided anything. Without the check here, a launch that
    // ends in "reconnect" still fires one doomed Drive call five seconds later.
    if (localStorage.getItem(RECONNECT_KEY) === "1") {
      setStatus("reconnect");
      return false;
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setStatus("offline");
      return false;
    }
    runningRef.current = true;
    setStatus("syncing");
    // Flips true once a real Drive call is under way. Read in the `finally`
    // below, for the automatic-trigger floor — see the comment down there.
    let attempted = false;
    try {
      const drive = await import(/* webpackChunkName: "drive" */ "./drive");

      attempted = true;
      let fileId = fileIdRef.current || (await drive.findFile());
      const remote = fileId ? await drive.readFile(fileId) : null;

      // A document from a newer version of the app is not ours to merge or
      // overwrite. Stop rather than clobber it.
      if (remote && remote.schemaVersion > SCHEMA_VERSION) {
        setStatus("error");
        return false;
      }

      // An older file is upgraded before merging, so the merge sees the current
      // shape. The upload check below still compares against the raw file, so
      // an old file on Drive is always replaced with the current version.
      const merged = merge(docRef.current, remote ? upgradeDoc(remote) : null);
      if (JSON.stringify(merged) !== JSON.stringify(docRef.current)) onMergedRef.current(merged);
      if (!fileId) fileId = await drive.createFile(merged);
      else if (JSON.stringify(merged) !== JSON.stringify(remote)) await drive.updateFile(fileId, merged);

      fileIdRef.current = fileId;
      setLastSyncedAt(Date.now());
      setStatus("synced");

      // Only after the sync above has actually succeeded, and only once: never
      // awaited, so a slow or failing lookup cannot delay or affect syncing.
      // Gated on localStorage rather than the `account` state/ref: a fresh
      // "ok" clears the key synchronously (see the boot effect and connect()
      // below), and reading localStorage here sees that immediately, with no
      // dependency on a re-render having happened first.
      if (!localStorage.getItem(ACCOUNT_KEY)) {
        drive.fetchAccountEmail().then((email) => {
          if (!email || !mountedRef.current) return;
          localStorage.setItem(ACCOUNT_KEY, email);
          setAccount(email);
        });
      }

      return true;
    } catch (error) {
      // Forget the cached id: if the file was deleted from another device every
      // later sync would 404 against it for ever.
      fileIdRef.current = null;
      if (error && error.name === "NeedsAuthError") {
        // Sticky only for this session (see needsAuthRef above) — deliberately
        // not RECONNECT_KEY, which would also block the next launch's silent
        // renewal and turn an hourly expiry into a permanent stop.
        needsAuthRef.current = true;
        setStatus("reconnect");
      } else {
        setStatus("error");
      }
      return false;
    } finally {
      runningRef.current = false;
      // The floor throttles automatic triggers by how many Drive requests
      // they can cause, and a failed request costs exactly as much as a
      // successful one — so a failed attempt must start the floor too, not
      // only a success. Gated on `attempted` rather than stamped
      // unconditionally: the four early returns above (in-flight, needs-auth,
      // pending-reconnect, offline) exit before `try` even starts, so they
      // never reach this `finally` and never stamp regardless — but if the
      // *first* line inside `try` itself fails (the dynamic import, say)
      // before any Drive call was made, `attempted` is still false, and this
      // stops that from stamping too. The offline case is the one that would
      // actually bite if any of this stamped unconditionally: it would put
      // the next `online` event inside the floor window and swallow the very
      // sync the user is waiting for.
      if (attempted) lastSyncCompletedAtRef.current = Date.now();
      // A tick made while this run was busy asked to be synced too. Run once
      // more now rather than waiting for an unrelated trigger; the flag is
      // cleared first so this cannot chain into a loop.
      if (rerunRequestedRef.current) {
        rerunRequestedRef.current = false;
        syncNow();
      }
    }
  }, []);

  const connect = useCallback(async () => {
    localStorage.setItem(ENABLED_KEY, "true");
    localStorage.removeItem(RECONNECT_KEY);
    localStorage.removeItem(RECONNECT_AT_KEY);
    // Reconnecting may switch accounts (the interactive screen lets the user
    // pick a different one); blank the label now rather than showing the old
    // address for the whole redirect round trip.
    localStorage.removeItem(ACCOUNT_KEY);
    setAccount(null);
    needsAuthRef.current = false;
    setEnabled(true);
    const drive = await import(/* webpackChunkName: "drive" */ "./drive");
    // No login_hint here, deliberately: a hint suppresses Google's account
    // chooser, and the interactive screen is the only way a user can switch
    // accounts. Only the silent renewal below hints — prompt=none can't show
    // a chooser at all, so ambiguity there means failure instead of a choice.
    drive.beginAuth({ silent: false }); // navigates away; nothing after this runs
  }, []);

  const disconnect = useCallback(async () => {
    localStorage.removeItem(ENABLED_KEY);
    localStorage.removeItem(RECONNECT_KEY);
    localStorage.removeItem(RECONNECT_AT_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
    needsAuthRef.current = false;
    setEnabled(false);
    setStatus("idle");
    setAccount(null);
    fileIdRef.current = null;
    const drive = await import(/* webpackChunkName: "drive" */ "./drive");
    drive.clearAuth();
  }, []);

  // Boot: read whatever the redirect left behind, then either sync or renew.
  useEffect(() => {
    // Do not even fetch the drive chunk unless sync is on, or the URL carries an
    // auth result to process. A visitor who never connects should download
    // nothing extra, on any launch. The chunk is same-origin so this is not the
    // external-requests promise — it is the "nothing changes when signed out"
    // one.
    // Must match PENDING_KEY in drive.js. Duplicated deliberately: reading it
    // here is what lets us skip loading the drive chunk at all.
    const hasPendingAuth = localStorage.getItem("syncAuthPending") !== null;
    if (!isSyncEnabled() && !hasPendingAuth) return undefined;

    let cancelled = false;
    (async () => {
      const drive = await import(/* webpackChunkName: "drive" */ "./drive");
      if (!drive.isConfigured()) return;

      const result = drive.readAuthResult();

      // Record the outcome BEFORE honouring `cancelled`. StrictMode mounts twice
      // in development, and the first run has already consumed the URL fragment.
      // Bailing here would leave the second run seeing "none", no token and no
      // reconnect flag — so it would redirect again, for ever.
      if (result === "ok") {
        localStorage.removeItem(RECONNECT_KEY);
        localStorage.removeItem(RECONNECT_AT_KEY);
        setReason(null);
        // A fresh token may belong to a different account than the cached label:
        // Reconnect lets the user choose, and a silent renewal follows whatever
        // Google session is current. Drop the label so the next successful sync
        // re-fetches it. Showing the wrong account is worse than showing none.
        localStorage.removeItem(ACCOUNT_KEY);
        setAccount(null);
      } else if (result === "needs-interaction" || result === "error") {
        localStorage.setItem(RECONNECT_KEY, "1");
        localStorage.setItem(RECONNECT_AT_KEY, String(Date.now()));
        // drive.js already wrote Google's error code, if it had one, as part of
        // producing this result — just pick it back up for the UI.
        setReason(localStorage.getItem(ERROR_KEY));
      } else if (drive.pendingAuth()) {
        // A redirect was started and never came back: a captive portal, a lost
        // tab, a force-quit mid-flight. Without this, every launch tries again
        // and the user never reaches their schedule.
        drive.clearPendingAuth();
        localStorage.setItem(RECONNECT_KEY, "1");
        localStorage.setItem(RECONNECT_AT_KEY, String(Date.now()));
        localStorage.setItem(ERROR_KEY, LOST_REDIRECT_REASON);
        setReason(LOST_REDIRECT_REASON);
      }

      if (cancelled) return;
      if (!isSyncEnabled()) return;

      if (localStorage.getItem(RECONNECT_KEY) === "1") {
        // A failed silent attempt suppresses further automatic ones — but only
        // for RECONNECT_EXPIRY_MS. Past that, one transient failure (Google
        // refusing once, a redirect that didn't complete) would otherwise latch
        // the app into "paused" forever, since nothing else ever clears this
        // flag. Missing timestamp counts as expired too, so a user stuck on a
        // build from before RECONNECT_AT_KEY existed recovers on next launch
        // instead of staying stuck for good.
        const failedAt = Number(localStorage.getItem(RECONNECT_AT_KEY));
        const expired = !failedAt || Date.now() - failedAt > RECONNECT_EXPIRY_MS;
        if (expired) {
          localStorage.removeItem(RECONNECT_KEY);
          localStorage.removeItem(RECONNECT_AT_KEY);
        } else {
          setStatus("reconnect");
          return;
        }
      }
      if (drive.storedToken()) {
        syncNow();
        return;
      }
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setStatus("offline");
        return;
      }
      // Hinting the cached account address helps prompt=none resolve which
      // session to use (see drive.js); it is simply absent on a first-ever
      // connect, when nothing is cached yet.
      drive.beginAuth({ silent: true, loginHint: localStorage.getItem(ACCOUNT_KEY) }); // navigates away
    })().catch(() => {
      // A rejection here (the dynamic import failing, say) must not become an
      // unhandled promise rejection, and must not leave an unstripped
      // #access_token=... sitting in the address bar and in history.
      if (!cancelled) setStatus("error");
    });
    return () => {
      cancelled = true;
    };
  }, [syncNow]);

  // Back to the foreground (tab switch, window focus), and back online.
  useEffect(() => {
    if (!enabled) return undefined;
    const again = () => {
      if (localStorage.getItem(RECONNECT_KEY) === "1") return;
      if (needsAuthRef.current) return;
      // Automatic triggers only — see AUTO_SYNC_FLOOR_MS above. `focus` can
      // fire on every alt-tab, so without this a burst of them would fire a
      // burst of syncs.
      if (Date.now() - lastSyncCompletedAtRef.current < AUTO_SYNC_FLOOR_MS) return;
      syncNow();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") again();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", again);
    // visibilitychange alone misses the most common way someone leaves and
    // returns to a desktop app: switching to another application while the
    // tab stays visible. That doesn't change visibilityState, but it does
    // blur and refocus the window.
    window.addEventListener("focus", again);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", again);
      window.removeEventListener("focus", again);
    };
  }, [enabled, syncNow]);

  // A few seconds after a local change. Also fires for a change that came FROM a
  // sync, which is harmless: merge is idempotent, so the follow-up uploads nothing.
  useEffect(() => {
    if (!enabled) return undefined;
    if (localStorage.getItem(RECONNECT_KEY) === "1") return undefined;
    if (needsAuthRef.current) return undefined;
    const timer = setTimeout(() => syncNow(), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [doc, enabled, syncNow]);

  return { enabled, status, lastSyncedAt, account, reason, connect, disconnect, syncNow };
}
